import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState, type ChangeEvent, type DragEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Extension fallback for browsers that don't report a MIME type for a drop.
const MIME_EXTENSIONS: Record<string, ReadonlyArray<string>> = {
    'image/png': ['png'],
    'image/jpeg': ['jpg', 'jpeg'],
    'application/pdf': ['pdf'],
};

/**
 * Only what a row renders. Structural on purpose, so the onboarding form and the settings panel can each pass their
 * own document shape without either becoming the other's dependency.
 */
export type UploadedDocument = {
    uuid: string;
    fileName: string;
    size: number | null;
};

type PendingUpload = {
    key: string;
    name: string;
    size: number;
    failed: boolean;
};

export type DocumentUploaderProps = {
    label: string;
    description?: string;
    acceptedMimeTypes: ReadonlyArray<string>;
    hint: string;
    maxSizeBytes: number;
    maxCount: number;
    documents: ReadonlyArray<UploadedDocument>;
    onUpload: (file: File) => Promise<void>;
    onRemove: (documentUuid: string) => Promise<void>;
    optional?: boolean;
    // While Partner Management is looking at the documents the list stays visible but nothing about it can change.
    readOnly?: boolean;
};

const DROP_ZONE_SX = {
    border: '1px dashed',
    borderColor: 'divider',
    borderRadius: 1,
    py: 4,
    px: 2,
    textAlign: 'center',
} as const;

const ROW_SX = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    px: 2,
    py: 1,
} as const;

function isTypeAccepted(file: File, allowed: ReadonlyArray<string>): boolean {
    if (allowed.length === 0) {
        return true;
    }
    if (file.type && allowed.includes(file.type)) {
        return true;
    }

    /* v8 ignore next -- splitting a non-empty string always yields a last part. */
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return allowed.some((mime) => MIME_EXTENSIONS[mime]?.includes(ext) ?? false);
}

function formatSize(bytes: number | null): string {
    if (bytes === null) {
        return '';
    }
    const kb = bytes / 1024;
    return kb < 1024 ? `${Math.max(1, Math.round(kb))} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

export function DocumentUploader({
    label,
    description,
    acceptedMimeTypes,
    hint,
    maxSizeBytes,
    maxCount,
    documents,
    onUpload,
    onRemove,
    optional = true,
    readOnly = false,
}: DocumentUploaderProps): ReactElement {
    const { t } = useTranslation();
    const [pending, setPending] = useState<ReadonlyArray<PendingUpload>>([]);
    const [error, setError] = useState<string | undefined>(undefined);
    const [dragActive, setDragActive] = useState(false);
    const [removing, setRemoving] = useState<ReadonlyArray<string>>([]);
    const maxMb = Math.round(maxSizeBytes / (1024 * 1024));

    const inFlight = pending.filter((upload) => !upload.failed).length;
    const used = documents.length + inFlight;
    const isFull = used >= maxCount;

    const uploadOne = async (file: File, key: string): Promise<void> => {
        try {
            await onUpload(file);
            setPending((previous) => previous.filter((upload) => upload.key !== key));
        } catch {
            setPending((previous) =>
                previous.map((upload) => (upload.key === key ? { ...upload, failed: true } : upload)),
            );
        }
    };

    const selectFiles = (selected: ReadonlyArray<File>): void => {
        let room = maxCount - used;
        const accepted: Array<{ file: File; key: string }> = [];
        let nextError: string | undefined = undefined;

        for (const file of selected) {
            if (room <= 0) {
                nextError = t('DOCUMENT_UPLOADER.ERRORS.TOO_MANY', { count: maxCount });
                break;
            }
            if (!isTypeAccepted(file, acceptedMimeTypes)) {
                nextError = t('DOCUMENT_UPLOADER.ERRORS.UNSUPPORTED', { hint });
                continue;
            }
            if (file.size > maxSizeBytes) {
                nextError = t('DOCUMENT_UPLOADER.ERRORS.TOO_LARGE', { mb: maxMb });
                continue;
            }
            accepted.push({ file, key: crypto.randomUUID() });
            room -= 1;
        }

        setError(nextError);
        if (accepted.length === 0) {
            return;
        }

        setPending((previous) => [
            ...previous,
            ...accepted.map(({ file, key }) => ({ key, name: file.name, size: file.size, failed: false })),
        ]);
        // Sequential: each response carries the whole list, so overlapping ones could land out of order and briefly
        // hide a document that is already stored.
        void (async (): Promise<void> => {
            for (const { file, key } of accepted) {
                await uploadOne(file, key);
            }
        })();
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        /* v8 ignore next -- a file input always reports a FileList, empty at worst. */
        const selected = Array.from(event.target.files ?? []);
        // Reset so picking the same file again re-fires change after a fix.
        event.target.value = '';
        selectFiles(selected);
    };

    const handleDragOver = (event: DragEvent<HTMLElement>): void => {
        event.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLElement>): void => {
        event.preventDefault();
        setDragActive(false);
    };

    const handleDrop = (event: DragEvent<HTMLElement>): void => {
        event.preventDefault();
        setDragActive(false);
        selectFiles(Array.from(event.dataTransfer.files));
    };

    const remove = async (documentUuid: string): Promise<void> => {
        setError(undefined);
        setRemoving((previous) => [...previous, documentUuid]);
        try {
            await onRemove(documentUuid);
        } catch {
            setError(t('DOCUMENT_UPLOADER.ERRORS.REMOVE_FAILED'));
        } finally {
            setRemoving((previous) => previous.filter((uuid) => uuid !== documentUuid));
        }
    };

    const discardPending = (key: string): void => {
        setPending((previous) => previous.filter((upload) => upload.key !== key));
    };

    return (
        <Stack spacing={1}>
            <Typography variant="subtitle2">
                {label}
                {optional && (
                    <>
                        {' '}
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            {t('DOCUMENT_UPLOADER.OPTIONAL_SUFFIX')}
                        </Box>
                    </>
                )}
            </Typography>

            {description && (
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            )}

            {documents.map((document) => (
                <Stack key={document.uuid} direction="row" spacing={1} sx={{ ...ROW_SX, alignItems: 'center' }}>
                    <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                            {document.fileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {[formatSize(document.size), t('DOCUMENT_UPLOADER.STATUS.COMPLETE')]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                    </Stack>
                    {!readOnly && (
                        <IconButton
                            size="small"
                            disabled={removing.includes(document.uuid)}
                            aria-label={t('DOCUMENT_UPLOADER.REMOVE_ARIA', { label: document.fileName })}
                            onClick={() => void remove(document.uuid)}>
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            ))}

            {pending.map((upload) => (
                <Stack key={upload.key} direction="row" spacing={1} sx={{ ...ROW_SX, alignItems: 'center' }}>
                    {upload.failed ? (
                        <ErrorOutlineIcon fontSize="small" color="error" />
                    ) : (
                        <CircularProgress size={16} />
                    )}
                    <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                            {upload.name}
                        </Typography>
                        <Typography variant="caption" color={upload.failed ? 'error' : 'text.secondary'}>
                            {[
                                formatSize(upload.size),
                                upload.failed
                                    ? t('DOCUMENT_UPLOADER.STATUS.FAILED')
                                    : t('DOCUMENT_UPLOADER.STATUS.UPLOADING'),
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                    </Stack>
                    {upload.failed && (
                        <IconButton
                            size="small"
                            aria-label={t('DOCUMENT_UPLOADER.REMOVE_ARIA', { label: upload.name })}
                            onClick={() => discardPending(upload.key)}>
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            ))}

            {!isFull && !readOnly && (
                <Box
                    component="label"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                        ...DROP_ZONE_SX,
                        borderColor: error ? 'error.main' : dragActive ? 'primary.main' : 'divider',
                        bgcolor: dragActive ? 'action.hover' : undefined,
                        position: 'relative',
                        display: 'block',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background-color 0.15s',
                        '&:focus-within': {
                            borderColor: error ? 'error.main' : 'primary.main',
                            outline: '2px solid',
                            outlineColor: error ? 'error.main' : 'primary.main',
                            outlineOffset: 2,
                        },
                    }}>
                    <Box
                        component="input"
                        type="file"
                        multiple={maxCount - used > 1}
                        accept={acceptedMimeTypes.join(',')}
                        aria-label={label}
                        aria-invalid={error !== undefined}
                        onChange={handleChange}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer',
                        }}
                    />
                    <Stack spacing={1} sx={{ alignItems: 'center', pointerEvents: 'none' }}>
                        <NoteAddOutlinedIcon color="primary" />
                        <Typography variant="body2">
                            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                                {t('DOCUMENT_UPLOADER.UPLOAD_LINK')}
                            </Box>{' '}
                            <Box component="span" sx={{ fontWeight: 600 }}>
                                {t('DOCUMENT_UPLOADER.DRAG_DROP')}
                            </Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {hint}
                        </Typography>
                    </Stack>
                </Box>
            )}

            {error && (
                <Typography role="alert" variant="caption" color="error">
                    {error}
                </Typography>
            )}
        </Stack>
    );
}
